import { useState, useEffect } from "react";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  User,
  BookOpen,
  DollarSign,
} from "lucide-react";
import { circulationApi, type FineRecord, type PaginatedResponse } from "@/lib/api/circulation";

const statusConfig: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
  PENDING: {
    label: "Pending",
    cls: "border border-amber-200 bg-amber-50 text-slate-900",
    icon: Clock,
  },
  PAID: { label: "Paid", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  WAIVED: { label: "Waived", cls: "bg-blue-100 text-blue-600", icon: Ban },
  CANCELLED: { label: "Cancelled", cls: "bg-gray-100 text-gray-500", icon: XCircle },
};

type StatusFilter = "all" | "PENDING" | "PAID" | "WAIVED";

const FinesManagementPage = () => {
  const toast = useToast();
  const [data, setData] = useState<PaginatedResponse<FineRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<StatusFilter>("PENDING");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Waive dialog
  const [waiveId, setWaiveId] = useState<string | null>(null);
  const [waiveNotes, setWaiveNotes] = useState("");
  const [waiveLoading, setWaiveLoading] = useState(false);

  useEffect(() => {
    loadFines();
  }, [tab, page]);

  const loadFines = async () => {
    setLoading(true);
    try {
      const status = tab === "all" ? undefined : tab;
      const result = await circulationApi.getFines({ status, page, limit: 30 });
      setData(result);
    } catch {
      toast.error("Failed to load fines");
    } finally {
      setLoading(false);
    }
  };

  const handleWaive = async () => {
    if (!waiveId) return;
    setWaiveLoading(true);
    try {
      await circulationApi.waiveFine(waiveId, waiveNotes || undefined);
      toast.success("Fine waived successfully");
      setWaiveId(null);
      setWaiveNotes("");
      loadFines();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to waive fine");
    } finally {
      setWaiveLoading(false);
    }
  };

  const filteredData = data?.data?.filter((f) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      f.user?.firstName?.toLowerCase().includes(q) ||
      f.user?.lastName?.toLowerCase().includes(q) ||
      f.user?.email?.toLowerCase().includes(q) ||
      f.reason?.toLowerCase().includes(q) ||
      f.checkout?.item?.biblio?.title?.toLowerCase().includes(q)
    );
  });

  const totalPending =
    data?.data?.reduce(
      (sum, f) => (f.status === "PENDING" ? sum + parseFloat(f.amount) : sum),
      0
    ) ?? 0;

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Fines & Fees"
          description={data ? `${data.total} total fine(s)` : "Loading..."}
          breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Fines & Fees" }]}
          showSearch={false}
          titleSize="md"
          actions={
            totalPending > 0 ? (
              <Card className="px-4 py-2 border-amber-200 bg-amber-50 shadow-none">
                <p className="text-sm text-slate-900">
                  Total Pending: <span className="font-bold">₦{totalPending.toFixed(2)}</span>
                </p>
              </Card>
            ) : undefined
          }
        />

        {/* Filter tabs + search */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex bg-muted/50 rounded-lg p-1 gap-1">
            {(["PENDING", "PAID", "WAIVED", "all"] as StatusFilter[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  tab === t
                    ? "bg-background shadow font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "all" ? "All" : (statusConfig[t]?.label ?? t)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by patron, title, reason..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No fines found</p>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Patron</th>
                    <th className="px-4 py-3 font-medium">Item</th>
                    <th className="px-4 py-3 font-medium">Reason</th>
                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredData.map((fine) => {
                    const cfg = statusConfig[fine.status] ?? statusConfig.PENDING;
                    return (
                      <tr key={fine.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="truncate max-w-[150px]">
                                {fine.user?.firstName} {fine.user?.lastName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {fine.user?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <p className="truncate max-w-[180px]">
                              {fine.checkout?.item?.biblio?.title ?? "—"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-[200px]">
                          <p className="truncate text-muted-foreground">{fine.reason}</p>
                        </td>
                        <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                          ₦{parseFloat(fine.amount).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={`${cfg.cls} text-xs`}>
                            {cfg.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {new Date(fine.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          {fine.status === "PENDING" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => setWaiveId(fine.id)}
                            >
                              Waive
                            </Button>
                          )}
                          {fine.status === "WAIVED" && fine.waivedAt && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(fine.waivedAt).toLocaleDateString()}
                            </span>
                          )}
                          {fine.status === "PAID" && fine.paidAt && (
                            <span className="text-xs text-muted-foreground">
                              Paid {new Date(fine.paidAt).toLocaleDateString()}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>

      {/* Waive dialog */}
      <Dialog
        open={!!waiveId}
        onOpenChange={() => {
          setWaiveId(null);
          setWaiveNotes("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Waive Fine</DialogTitle>
            <DialogDescription>This action will be recorded for audit purposes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Textarea
              value={waiveNotes}
              onChange={(e) => setWaiveNotes(e.target.value)}
              placeholder="Reason for waiving (optional)..."
              rows={3}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setWaiveId(null);
                  setWaiveNotes("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleWaive} disabled={waiveLoading}>
                {waiveLoading ? <LoadingSpinner /> : "Waive Fine"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default FinesManagementPage;
